import { landedHover } from '@src/services/dubbing/redubLanded';
import { useEffect, useRef } from 'react';

import { useDubPassagePlayback } from './useDubPassagePlayback';
import { useDubManifestFetch } from './useDubbedLanguages';

import type { RedubLanded } from '@src/services/dubbing/redubLanded';

/**
 * PLAT-12024 — lights up the words a re-dub just re-recorded, in the transcript
 * it is rendered inside, and says on hover what was asked for and when.
 *
 * Rendered by a host through `Transcript`'s `belowHeader` slot, so it sits
 * inside that transcript's own DOM and finds its words there — never by a
 * global id, since two transcripts can be on screen at once (the clip preview
 * beside the story's). It renders nothing visible of its own: the pulse is the
 * `redub-landed` class on the translated word atoms, which carry the line they
 * belong to as `data-segment-id`.
 *
 * - On a new landing (`landed.at` changes): the first re-dubbed word scrolls
 *   into view, every re-dubbed word pulses for as long as a saved clip card
 *   does, and each carries a hover saying when it was re-dubbed and with what
 *   direction (read from the stored dub, so it reflects what was made).
 * - On the toast's action (`landed.revealToken` changes): the same, plus the
 *   passage plays on its own, so "where did it go?" and "what did it do?" are
 *   one press.
 * - Only changes seen while mounted count. A pulse mounted later — the review
 *   opened again, or a host's transcript back after the review closed —
 *   starts from the landing it is given and does nothing for it: no light,
 *   no scroll, no sound. Each landing and each reveal plays once, in the
 *   transcript on screen when it happened.
 */
interface RedubLandedPulseProps {
  storyId: string;
  /** The dubbed language's code (BCP-47). */
  language: string;
  landed: RedubLanded | null;
}

/** The saved-clip card's highlight length, so the two read as one treatment. */
export const REDUB_LANDED_PULSE_MS = 6000;

/** The wash on a line's words; also the review's "here is your passage" on a hand-off (PLAT-12047). */
export const REDUB_LANDED_CLASS = 'redub-landed';
const LANDED_CLASS = REDUB_LANDED_CLASS;

/**
 * The nearest ancestor that holds translated words — this transcript, not any
 * other on the page. Walks up from the slot rather than reaching for the
 * shared `#transcript-scrollable-container` id, which is not unique.
 */
export function transcriptRootOf(
  start: HTMLElement | null,
): HTMLElement | null {
  let node: HTMLElement | null = start;
  while (node && node !== document.body) {
    if (node.querySelector('[data-segment-id]')) return node;
    node = node.parentElement;
  }
  return null;
}

/** Every translated word of these lines inside `root` (PLAT-12024). */
export function wordsOf(root: HTMLElement, segmentIds: readonly string[]) {
  const selector = segmentIds
    .map(id => `[data-segment-id="${CSS.escape(id)}"]`)
    .join(',');
  return selector
    ? Array.from(root.querySelectorAll<HTMLElement>(selector))
    : [];
}

const RedubLandedPulse = ({
  storyId,
  language,
  landed,
}: RedubLandedPulseProps) => {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const fetchManifest = useDubManifestFetch();
  const passage = useDubPassagePlayback({
    storyId,
    language,
    segmentIds: landed?.segmentIds ?? [],
  });
  const toggleRef = useRef(passage.toggle);
  toggleRef.current = passage.toggle;

  const at = landed?.at ?? null;
  const revealToken = landed?.revealToken ?? 0;
  const segmentIdsKey = (landed?.segmentIds ?? []).join(',');
  // What this pulse has already seen, starting with what it was mounted with:
  // a landing that predates the mount is not acted on.
  const seenRef = useRef({ at, revealToken });
  const revealSeenRef = useRef(revealToken);
  // Read when a landing is acted on, never a reason to act again: a host
  // re-render or a language switch is not a new landing, and re-running on it
  // would cancel a look still pending.
  const readRef = useRef({ storyId, language, fetchManifest });
  readRef.current = { storyId, language, fetchManifest };

  useEffect(() => {
    const seen = seenRef.current;
    seenRef.current = { at, revealToken };
    if (seen.at === at && seen.revealToken === revealToken) return undefined;
    if (!at || !segmentIdsKey) return undefined;
    const segmentIds = segmentIdsKey.split(',');
    const root = transcriptRootOf(anchorRef.current);
    if (!root) return undefined;
    // The words may not be rendered on the very frame the landing is set (the
    // host re-reads the dub first), so look once now and once more a beat
    // later; whichever finds them wins and the other is a no-op.
    let timer: ReturnType<typeof setTimeout> | null = null;
    let retry: ReturnType<typeof setTimeout> | null = null;
    let lit: HTMLElement[] = [];
    const light = () => {
      const words = wordsOf(root, segmentIds);
      if (words.length === 0) return false;
      lit = words;
      for (const word of words) word.classList.add(LANDED_CLASS);
      words[0].scrollIntoView({ block: 'center', behavior: 'smooth' });
      timer = setTimeout(() => {
        for (const word of words) word.classList.remove(LANDED_CLASS);
      }, REDUB_LANDED_PULSE_MS);
      return true;
    };
    if (!light()) retry = setTimeout(light, 400);

    // The hover reads what the dub now records for each line. Set once the
    // manifest answers; left in place until the next landing on that line.
    const controller = new AbortController();
    const read = readRef.current;
    void read
      .fetchManifest({ storyId: read.storyId, language: read.language })
      .then(resolved => {
        if (controller.signal.aborted) return;
        const manifest = resolved?.manifest ?? null;
        for (const word of wordsOf(root, segmentIds)) {
          const hover = landedHover(manifest, word.dataset.segmentId ?? '');
          if (hover) word.title = hover;
        }
      })
      .catch(() => {
        // No record to read: the words still pulse, they just say nothing on
        // hover. Nothing is invented.
      });

    return () => {
      controller.abort();
      if (timer) clearTimeout(timer);
      if (retry) clearTimeout(retry);
      for (const word of lit) word.classList.remove(LANDED_CLASS);
    };
  }, [at, revealToken, segmentIdsKey]);

  // The toast's action: back to the passage, and hear it.
  useEffect(() => {
    if (revealToken === revealSeenRef.current) return;
    revealSeenRef.current = revealToken;
    if (!revealToken) return;
    toggleRef.current();
  }, [revealToken]);

  // Nothing of its own to show; the span is only the foothold in this
  // transcript's DOM.
  return <span ref={anchorRef} data-testid="redub-landed-anchor" hidden />;
};

export default RedubLandedPulse;
